import classNames from 'clsx'
import { twMerge } from 'tailwind-merge'
import { ChangeEvent, ComponentProps, ReactNode } from 'react'
import { useFormContext, useController } from 'react-hook-form'

import { MessageError } from 'components/Form'

interface TextAreaProps extends ComponentProps<'textarea'> {
  label?: ReactNode | string
}

export const TextArea = ({
  name,
  maxLength = 5000,
  className = '',
  ...rest
}: TextAreaProps) => {
  const { control } = useFormContext()
  const {
    field: { value, ...field },
    fieldState
  } = useController({ name, control, defaultValue: '' })

  const messageError = fieldState.error?.message
  const isError = typeof messageError === 'string'

  const handleChangeInput = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    const formatValue = event.target.value.trimStart()
    field.onChange(formatValue)
  }

  return (
    <fieldset
      className={twMerge('flex flex-col gap-1 w-full items-start', className)}
    >
      {rest?.label && (
        <label
          htmlFor={name}
          className={classNames(
            'text-body2 font-regular',
            isError && 'text-error-500',
            rest?.disabled && 'text-font-color-disabled'
          )}
        >
          {rest.label}
          {rest?.required && <span className="text-error-500">*</span>}
        </label>
      )}
      <textarea
        rows={4}
        maxLength={maxLength}
        {...rest}
        {...field}
        name={name}
        value={value}
        onChange={handleChangeInput}
        className={classNames(
          'w-full bg-transparent py-3 px-4 rounded-lg text-font-color-primary placeholder:text-font-color-disabled text-body1 border border-gray-500/[.32] hover:border-gray-800 text-gray-800 outline-1 outline-primary disabled:cursor-not-allowed disabled:text-font-color-disabled',
          isError &&
            'border-2 border-error-500 outline-transparent hover:border-error-500'
        )}
      />
      <div className="w-full flex mt-1">
        <MessageError>{messageError}</MessageError>
        <span className="ml-auto text-caption text-gray-500">
          {value.length}/{maxLength}
        </span>
      </div>
    </fieldset>
  )
}
